import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PropertyService } from '../../services/property.service';
import { NavigationContextService } from '../../services/navigation-context.service';

@Component({
  selector: 'app-lease-document-pdf',
  templateUrl: './lease-document-pdf.component.html',
  styleUrls: ['./lease-document-pdf.component.scss']
})
export class LeaseDocumentPdfComponent implements OnInit, OnDestroy {
  loadError = false;
  objectUrl: string;
  fileName = 'lease.pdf';
  isPdf = true;
  isImage = false;
  sharing = false;
  canShare = false;
  /** File bytes, rendered inline (PDF) and reused for share/download. */
  fileBytes: ArrayBuffer;
  /** pdf.js could not draw the PDF; show the Open/Share/Download panel. */
  renderFailed = false;
  private mimeType = 'application/pdf';
  private propertyId: string;
  private documentId: string;
  private leaseId: string;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private propertyService: PropertyService,
    private navContext: NavigationContextService
  ) { }

  ngOnInit() {
    this.canShare = typeof navigator !== 'undefined' && !!(navigator as any).share;
    this.propertyId = this.route.snapshot.paramMap.get('propertyId');
    this.documentId = this.route.snapshot.paramMap.get('documentId');
    this.leaseId = this.route.snapshot.queryParamMap.get('leaseId');
    const qpName = this.route.snapshot.queryParamMap.get('fileName');
    if (qpName) {
      this.fileName = qpName;
    }

    if (!this.propertyId || !this.documentId) {
      this.loadError = true;
      return;
    }

    this.propertyService.getLeaseDocumentBlob(this.propertyId, this.documentId).then((blob: Blob) => {
      return this.readBlobAsArrayBuffer(blob).then((buffer) => {
        this.fileBytes = buffer.slice(0);
        this.mimeType = (blob && blob.type) ? blob.type : this.guessMimeFromName(this.fileName);
        this.isPdf = this.mimeType.indexOf('pdf') >= 0 || this.looksLikePdf(buffer);
        this.isImage = !this.isPdf && this.mimeType.indexOf('image/') === 0;
        if (!this.isPdf && !this.isImage) {
          this.mimeType = this.mimeType || 'application/octet-stream';
        }
        const outBlob = new Blob([this.fileBytes], { type: this.mimeType || 'application/pdf' });
        this.objectUrl = URL.createObjectURL(outBlob);
      });
    }).catch((err) => {
      console.error('Error loading lease document:', err);
      this.loadError = true;
    });
  }

  get backLabel(): string {
    return this.leaseId ? 'Back to lease' : 'Back to leases';
  }

  private guessMimeFromName(name: string): string {
    const lower = (name || '').toLowerCase();
    if (lower.endsWith('.pdf')) return 'application/pdf';
    if (lower.endsWith('.png')) return 'image/png';
    if (lower.endsWith('.webp')) return 'image/webp';
    if (lower.endsWith('.gif')) return 'image/gif';
    if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg';
    return 'application/pdf';
  }

  private looksLikePdf(buffer: ArrayBuffer): boolean {
    if (!buffer || buffer.byteLength < 5) {
      return false;
    }
    const headerBytes = new Uint8Array(buffer, 0, 5);
    let header = '';
    for (let i = 0; i < headerBytes.length; i++) {
      header += String.fromCharCode(headerBytes[i]);
    }
    return header.indexOf('%PDF') === 0;
  }

  private readBlobAsArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = () => reject(reader.error || new Error('Failed to read document blob'));
      reader.readAsArrayBuffer(blob);
    });
  }

  private buildShareMessage(): string {
    return 'Lease document: ' + (this.fileName || 'lease.pdf');
  }

  /** Open PDF in the system viewer (same tab so browser Back returns here). */
  openInViewer() {
    if (!this.objectUrl) {
      return;
    }
    window.location.href = this.objectUrl;
  }

  download() {
    if (!this.fileBytes) {
      return;
    }
    const blob = new Blob([this.fileBytes], { type: this.mimeType || 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = this.fileName || 'lease.pdf';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  share() {
    if (!this.fileBytes || this.sharing) {
      return;
    }
    const n: any = navigator;
    if (!n || typeof n.share !== 'function') {
      this.download();
      return;
    }

    const message = this.buildShareMessage();
    const file = new File([this.fileBytes.slice(0)], this.fileName || 'lease.pdf', {
      type: this.mimeType || 'application/pdf',
      lastModified: Date.now()
    });

    this.sharing = true;
    const finish = () => { this.sharing = false; };
    const canShareFiles = !n.canShare || n.canShare({ files: [file] });
    const sharePromise = canShareFiles
      ? n.share({ files: [file], text: message, title: message })
      : n.share({ text: message, title: message });
    sharePromise.then(finish).catch((err) => {
      finish();
      if (err && err.name === 'AbortError') {
        return;
      }
      console.error('Error sharing lease document:', err);
      this.download();
    });
  }

  ngOnDestroy() {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
    }
  }

  back() {
    if (this.leaseId && this.propertyId) {
      this.router.navigate(['/modify-lease'], {
        queryParams: { propertyId: this.propertyId, leaseId: this.leaseId }
      });
      return;
    }
    const propertyId = this.propertyId || this.navContext.propertyId;
    if (propertyId) {
      this.navContext.goToManageLeases(propertyId);
      return;
    }
    this.router.navigate(['/manage-leases']);
  }
}
